import { api, unwrap } from '../../shared/api/client';
import type { components } from '../../shared/api/schema.gen';
import { LOOKBACK_MS, type TIssueDetail } from './api';

export type TIssueRunPage = components['schemas']['RunListResDto'];
export type TIssueRun = TIssueRunPage['data'][number];
export type TIssueCopy = TIssueRun['copies'][number];

// A flow step's failures are copies: `/v1/runs` lists them.
export const issueRuns = (
  issue: TIssueDetail,
  cursor: string | undefined,
  limit: number,
): Promise<TIssueRunPage> =>
  unwrap(
    api.GET('/v1/runs', {
      params: {
        query: {
          issueId: [issue.id],
          copyStatus: ['failed', 'retrying'],
          from: new Date(new Date(issue.firstSeen).getTime() - LOOKBACK_MS).toISOString(),
          to: new Date(Date.now() + 1000).toISOString(),
          cursor,
          limit,
        },
      },
    }),
  );

export type TFlowIssueKind = 'removed' | 'rate' | 'merge' | 'schedule' | 'ai_budget' | 'failure';

export const flowIssueKind = (issue: TIssueDetail): TFlowIssueKind => {
  if (issue.removedInVersion !== null && issue.removedInVersion !== undefined) {
    return 'removed';
  }
  if (issue.errorClass === 'status_429') return 'rate';
  if (issue.errorClass === 'join_timeout') return 'merge';
  if (issue.errorClass === 'schedule_skipping') return 'schedule';
  if (issue.errorClass === 'ai.budget_exhausted') return 'ai_budget';
  return 'failure';
};

export const REDELIVERABLE: Record<TFlowIssueKind, boolean> = {
  removed: true,
  rate: true,
  merge: false,
  schedule: false,
  ai_budget: false,
  failure: true,
};

export const HISTOGRAM_BIN_MS = 15 * 60_000;
export const HISTOGRAM_BINS = 32;

// Server bins summed into 15-minute ones; coarser server bins cannot be split, so they win.
export const histogram = (
  issue: TIssueDetail,
  now: number,
): { values: number[]; binMs: number; start: number; end: number } => {
  const binMs = Math.max(HISTOGRAM_BIN_MS, issue.binMs);
  const end = Math.ceil(now / binMs) * binMs;
  const start = end - binMs * HISTOGRAM_BINS;
  const values = new Array<number>(HISTOGRAM_BINS).fill(0);
  for (const bin of issue.bins) {
    const at = Math.floor((bin.t - start) / binMs);
    if (at >= 0 && at < HISTOGRAM_BINS) values[at] += bin.n;
  }
  return { values, binMs, start, end };
};
